import {
  ciParaguayRegex,
  cuitArgentinaRegex,
  dniArgentinaRegex,
  dniNieSpainRegex,
  nifSpainRegex,
  nitRegex,
  nuipRegex,
  rucParaguayRegex,
  rucRegex,
  rutRegex,
} from './validations';

export const module11 = (taxIdNumber: string) => {
  const taxIdNumberReversed = taxIdNumber.split('').reverse();
  const verificationDigit = taxIdNumberReversed.reduce(
    (acc, number, index) => acc + Number(number) * ((index % 6) + 2),
    0,
  );

  return 11 - (verificationDigit % 11);
};

export const getVerificationDigitRutChile = (verificationDigit: number) => {
  if (verificationDigit === 11) return '0';
  if (verificationDigit === 10) return 'K';
  return verificationDigit.toString();
};

export const checkRutChile = (rut: string) => {
  if (rut === '') return true;
  if (!rutRegex.test(rut)) return false;
  const rutSplit = rut.split('-');
  const rutNumbers = rutSplit[0].replace(/\./g, '');
  const rutVerificationDigit = rutSplit[1];
  return rutVerificationDigit === getVerificationDigitRutChile(module11(rutNumbers));
};

export const checkRucPeru = (ruc: string) => {
  if (ruc === '') return true;
  if (!rucRegex.test(ruc)) return false;
  const rucNumber = ruc.slice(0, 10);
  const rucVerificationDigit = ruc.slice(-1);
  return rucVerificationDigit === (module11(rucNumber) % 10).toString();
};

export const checkNitColombia = (nit: string) => {
  const weights = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];
  if (nit === '') return true;
  if (!nitRegex.test(nit)) return false;
  const nitFormatted = nit.replace(/\./g, '');
  const nitNumbers = nitFormatted.slice(0, -2);
  const nitVerificationDigit = parseInt(nitFormatted.slice(-1));
  const nitLength = nitNumbers.length;
  if (nitLength > 15) return false;
  const sum = [...nitNumbers].reduce((acc, digit, i) => {
    const weight = weights[nitLength - i - 1];
    return acc + parseInt(digit) * weight;
  }, 0);
  const verificationDigit = sum % 11 > 1 ? 11 - (sum % 11) : sum % 11;
  return verificationDigit === nitVerificationDigit;
};

export const checkNuipColombia = (nuip: string) => {
  if (nuip === '') return true;
  const nuipFormatted = nuip.replace(/\./g, '');
  if (nuipFormatted.length > 10 || nuipFormatted.length < 6) return false;
  return nuipRegex.test(nuipFormatted);
};

export const checkDniArgentina = (dni: string) => {
  if (dni === '') return true;
  if (!dniArgentinaRegex.test(dni)) return false;
  return true;
};

export const checkCuitArgentina = (cuit: string) => {
  if (cuit === '') return true;
  if (!cuitArgentinaRegex.test(cuit)) return false;

  const [typeCode, numberPart, verifierDigitStr] = cuit.split('-');
  const fullNumber = (typeCode + numberPart).split('').map(Number);
  const verifierDigit = Number(verifierDigitStr);

  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sumWeights = 0;
  fullNumber.forEach((digit, index) => {
    if (!weights[index]) return;
    sumWeights += digit * weights[index];
  });
  const mod11 = 11 - (sumWeights % 11);
  const expectedDigit = mod11 === 11 ? 0 : mod11 === 10 ? 9 : mod11;
  return verifierDigit === expectedDigit;
};

export const checkCiParaguay = (ci: string) => {
  if (ci === '') return true;
  const ciFormatted = ci.replace(/\./g, '');
  if (!ciParaguayRegex.test(ciFormatted)) return false;
  return true;
};

export function checkRucParaguay(ruc: string) {
  if (ruc === '') return true;
  const rucFormatted = ruc.replace(/\./g, '');
  if (!rucParaguayRegex.test(rucFormatted)) return false;
  return true;
}

export const checkDniNieSpain = (input: string) => {
  if (input === '') return true;
  const dniNieSpainFormatted = input.toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (!dniNieSpainRegex.test(dniNieSpainFormatted)) return false;
  return true;
};

export function checkNifSpain(nif: string) {
  if (nif === '') return true;
  const nifSpainFormatted = nif.toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (!nifSpainRegex.test(nifSpainFormatted)) return false;
  return true;
}

export const formatRutChile = (input: string) => {
  // ignore all characters except numbers and k/K &
  // ignore possible zeroes at the beginning of the number
  const formattedIDNumber = input.replace(/[^0-9kK]/g, '').replace(/^0+/, '');
  const idNumbers = formattedIDNumber.slice(0, -1);
  const verificationDigit = formattedIDNumber.slice(-1).toUpperCase();
  if (idNumbers === '' && verificationDigit === '') return '';
  // Add points to the RUT number
  const numbers = idNumbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  // Join the formatted number and verification digit with a hyphen
  return numbers + '-' + verificationDigit;
};

export const formatRucPeru = (input: string) => {
  const formattedRuc = input.replace(/[^0-9]/g, '');
  if (formattedRuc === '') return '';
  return formattedRuc;
};

export const formatNitColombia = (input: string) => {
  const formattedIDNumber = input.replace(/[^0-9]/g, '').replace(/^0+/, '');
  const idNumbers = formattedIDNumber.slice(0, -1);
  const verificationDigit = formattedIDNumber.slice(-1);
  if (idNumbers === '' && verificationDigit === '') return '';
  // Add points to the RUT number
  const numbers = idNumbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  // Join the formatted number and verification digit with a hyphen
  return numbers + '-' + verificationDigit;
};

export const formatNuipColombia = (input: string) => {
  const formattedNuip = input.replace(/[^0-9]/g, '');
  return formattedNuip;
};

export const formatDniArgentina = (input: string) => {
  const formattedDni = input.replace(/[^0-9]/g, '');
  if (formattedDni === '') return '';
  return formattedDni;
};

export const formatCuitArgentina = (input: string) => {
  const digits = input.replace(/\D/g, '');
  if (digits.length < 11) return digits;

  const prefix = digits.slice(0, 2);
  const number = digits.slice(2, 10);
  const checkDigit = digits.slice(10, 11);

  return `${prefix}-${number}-${checkDigit}`;
};

export const formatCiParaguay = (input: string) => {
  const formattedCi = input.replace(/[^0-9]/g, '');
  if (formattedCi === '') return '';
  return formattedCi;
};

export const formatRucParaguay = (input: string) => {
  const clean = input.replace(/[^0-9]/g, '');

  if (clean.length < 7) return clean;

  const mainPart = clean.slice(0, -1);
  const verifier = clean.slice(-1);

  return `${mainPart}-${verifier}`;
};

export const formatDniNieSpain = (input: string) => {
  const formattedDniNie = input.toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (!formattedDniNie) return '';
  return formattedDniNie;
};

export const formatNifSpain = (input: string) => {
  const formattedNif = input.toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (!formattedNif) return '';
  return formattedNif;
};

export const checkPersonalIdOtherCountries = (country: string, input: string): boolean => {
  if (input === '') return true;
  switch (country) {
    case 'MX':
      return true;
    case 'BR':
      return /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/.test(input);
    case 'CO':
      return /^\d{5,10}$/.test(input);
    case 'UY':
      return /^\d{1,2}\.?\d{3}\.?\d{3}$/.test(input);
    case 'PY':
      return true;
    case 'BO':
      return true;
    case 'EC':
      return /^\d{10}$/.test(input);
    case 'VE':
      return /^[VE]-?\d{6,9}$/.test(input);
    case 'US':
      return /^\d{3}-?\d{2}-?\d{4}$/.test(input);
    case 'CA':
      return /^\d{3}\s?\d{3}\s?\d{3}$/.test(input);
    case 'DO':
      return /^\d{3}-?\d{7}-?\d$/.test(input);
    case 'CR':
      return /^\d{9}$/.test(input);
    case 'PA':
      return true;
    case 'GT':
      return /^\d{13}$/.test(input);
    case 'SV':
      return /^\d{8}-\d$/.test(input);
    case 'HN':
      return /^\d{4}-?\d{4}-?\d{5}$/.test(input);
    case 'NI':
      return /^\d{3}-?\d{6}-?\d{4}[A-Z]?$/.test(input);
    case 'PR':
      return /^\d{3}-?\d{2}-?\d{4}$/.test(input);
    case 'CU':
      return /^\d{2}\d{2}\d{2}\d{5}$/.test(input);
    case 'JM':
      return /^\d{9}$/.test(input);
    case 'ES':
      return /^\d{8}[A-Z]$/.test(input) || /^[XYZ]\d{7}[A-Z]$/.test(input);
    case 'PT':
      return /^\d{9}$/.test(input);
    case 'FR':
      return /^[12]\d{2}\d{2}\d{2}\d{3}\d{3}\d{2}$/.test(input);
    case 'DE':
      return /^\d{11}$/.test(input) || /^[A-Z0-9]{9}$/.test(input);
    case 'IT':
      return /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(input);
    case 'GB':
      return /^[A-CEGHJ-PR-TW-Z]{2}\d{6}[A-D]$/.test(input);
    case 'IE':
      return /^\d{7}[A-W][A-IW]?$/.test(input);
    case 'NL':
      return /^\d{9}$/.test(input);
    case 'BE':
      return /^\d{2}\.?\d{2}\.?\d{2}-?\d{3}\.?\d{2}$/.test(input);
    case 'CH':
      return /^756\.\d{4}\.\d{4}\.\d{2}$/.test(input);
    case 'AT':
      return /^\d{4}\s?\d{6}$/.test(input);
    case 'SE':
      return /^\d{6}[-+]\d{4}$/.test(input) || /^\d{8}-\d{4}$/.test(input);
    case 'NO':
      return /^\d{11}$/.test(input);
    case 'DK':
      return /^\d{6}-\d{4}$/.test(input);
    case 'FI':  
      return /^\d{6}[A+-]\d{3}[0-9A-Y]$/.test(input);
    case 'IS':
      return /^\d{6}-\d{4}$/.test(input);
    case 'PL':
      return /^\d{11}$/.test(input);
    case 'CZ':
      return /^\d{6}\/?\d{3,4}$/.test(input);
    case 'SK':
      return /^\d{6}\/\d{3,4}$/.test(input);
    case 'HU':
      return /^\d{9}$/.test(input);
    case 'RO':
      return /^\d{13}$/.test(input);
    case 'BG':
      return /^\d{10}$/.test(input);
    case 'GR':
      return /^\d{11}$/.test(input) || /^\d{9}$/.test(input);
    case 'HR':
      return /^\d{11}$/.test(input);
    case 'SI':
      return /^\d{13}$/.test(input);
    case 'RS':
      return /^\d{13}$/.test(input);
    case 'BA':
      return /^\d{13}$/.test(input);
    case 'ME':
      return /^\d{13}$/.test(input);
    case 'MK':
      return /^\d{13}$/.test(input);
    case 'MT':
      return /^[A-Z]\d{5}[A-Z]$/.test(input);
    case 'CY':
      return /^\d{8}$/.test(input);
    case 'LU':
      return /^\d{13}$/.test(input);
    case 'LT':
      return /^\d{11}$/.test(input);
    case 'LV':
      return /^\d{6}-\d{5}$/.test(input);
    case 'EE':
      return /^\d{11}$/.test(input);
    case 'TR':
      return /^[1-9]\d{10}$/.test(input);
    case 'IL':
      return /^\d{9}$/.test(input);
    case 'SA':
      return /^1\d{9}$/.test(input) || /^2\d{9}$/.test(input);
    case 'AE':
      return /^784-\d{4}-\d{7}-\d$/.test(input);
    case 'QA':
      return /^\d{11}$/.test(input);
    case 'KW':
      return /^\d{12}$/.test(input);
    case 'BH':
      return /^\d{2,3}-?\d{2}-?\d{4}$/.test(input);
    case 'OM':
      return /^\d{9}$/.test(input);
    case 'JO':
      return /^\d{10}$/.test(input);
    case 'LB':
      return /^\d{8}$/.test(input);
    case 'EG':
      return /^\d{14}$/.test(input);
    case 'MA':
      return /^[A-Z]{1,2}\d{3,6}$/.test(input);
    case 'TN':
      return /^\d{8}$/.test(input);
    case 'DZ':
      return true;
    case 'ZA':
      return /^\d{13}$/.test(input);
    case 'NG':
      return /^\d{11}$/.test(input);
    case 'KE':
      return /^\d{5,8}$/.test(input);
    case 'GH':
      return /^GHA-\d{9}-\d$/.test(input);
    case 'UG':
      return true;
    case 'TZ':
      return /^\d{20}$/.test(input);
    case 'RW':
      return /^\d{16}$/.test(input);
    case 'BW':
      return /^\d{9}$/.test(input);
    case 'ZM':
      return /^\d{6}\/\d{2}\/\d$/.test(input);
    case 'ZW':
      return /^\d{2}-\d{6,7}[A-Z]\d$/.test(input);
    case 'ET':
      return true;
    case 'CN':
      return /^\d{6}(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/.test(input);
    case 'IN':
      return /^\d{12}$/.test(input);
    case 'PK':
      return /^\d{5}-\d{7}-\d$/.test(input);
    case 'BD':
      return /^\d{10,17}$/.test(input);
    case 'JP':
      return /^\d{12}$/.test(input);
    case 'KR':
      return /^\d{6}-\d{7}$/.test(input);
    case 'SG':
      return /^[STFG]\d{7}[A-Z]$/.test(input);
    case 'MY':
      return /^\d{6}-\d{2}-\d{4}$/.test(input);
    case 'ID':
      return /^\d{16}$/.test(input);
    case 'PH':
      return /^\d{4}-\d{4}-\d{4}-\d{4}$/.test(input);
    case 'TH':
      return /^\d{13}$/.test(input);
    case 'VN':
      return /^\d{12}$/.test(input);
    case 'HK':
      return /^[A-Z]{1,2}\d{6}\([0-9A]\)$/.test(input);
    case 'TW':
      return /^[A-Z][12]\d{8}$/.test(input);
    case 'LK':
      return /^\d{12}$/.test(input) || /^\d{9}[VXvx]$/.test(input);
    case 'NP':
      return true;
    case 'AU':
      return /^\d{8,9}$/.test(input) || /^\d{4}\s?\d{5}\s?\d$/.test(input);
    case 'NZ':
      return /^[A-HJ-NP-Z]{3}\d{2}[A-D]$/.test(input) || /^\d{8,9}$/.test(input);
    case 'RU':
      return /^\d{3}-\d{3}-\d{3}\s\d{2}$/.test(input);
    case 'UA':
      return /^\d{10}$/.test(input);
    case 'BY':
      return true;
    case 'KZ':
      return /^\d{12}$/.test(input);
    case 'UZ':
      return /^\d{14}$/.test(input);
    case 'GE':
      return /^\d{11}$/.test(input);
    case 'AM':
      return /^\d{10}$/.test(input);
    case 'AZ':
      return /^[A-Z0-9]{7}$/.test(input);
    default:
      return true;
  }
};

export function checkTaxIdOtherCountries(country: string, input: string): boolean {
  if (input === '') return true;
  switch (country) {
    case 'MX':
      return true;
    case 'BR':
      return /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/.test(input);
    case 'CO':
      return /^\d{9,10}-\d$/.test(input);
    case 'UY':
      return /^\d{12}$/.test(input);
    case 'PY':
    case 'BO':
      return true;
    case 'EC':
      return /^\d{13}$/.test(input);
    case 'VE':
      return true;
    case 'US':
      return /^\d{2}-?\d{7}$/.test(input);
    case 'CA':
      return true;
    case 'DO':
    case 'CR':
    case 'PA':
    case 'GT':
    case 'SV':
    case 'HN':
    case 'NI':
    case 'PR':
    case 'CU':
    case 'JM':
      return true;

    // Europe
    case 'ES':
      return /^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(input);
    case 'PT':
      return /^\d{9}$/.test(input);
    case 'FR':
      return /^\d{9}$/.test(input) || /^\d{14}$/.test(input);
    case 'DE':
      return /^DE\d{9}$/.test(input);
    case 'IT':
      return /^\d{11}$/.test(input);
    case 'GB':
      return /^(\d{8}|[A-Z]{2}\d{6})$/.test(input);
    case 'IE':
      return /^\d{6,7}$/.test(input);
    case 'NL':
      return /^\d{8}$/.test(input);
    case 'BE':
      return /^\d{10}$/.test(input);
    case 'CH':
      return /^CHE-\d{3}\.\d{3}\.\d{3}$/.test(input);
    case 'AT':
      return /^ATU\d{8}$/.test(input);
    case 'SE':
      return /^\d{6}-\d{4}$/.test(input);
    case 'NO':
      return /^\d{9}$/.test(input);
    case 'DK':
      return /^\d{8}$/.test(input);
    case 'FI':
      return /^\d{7}-\d$/.test(input);
    case 'IS':
      return true;
    case 'PL':
      return /^\d{10}$/.test(input) || /^\d{9}(\d{5})?$/.test(input);
    case 'CZ':
      return /^\d{8}$/.test(input);
    case 'SK':
      return true;
    case 'HU':
      return /^\d{8}-\d-\d{2}$/.test(input);
    case 'RO':
      return /^\d{2,10}$/.test(input);
    case 'BG':
      return /^\d{9}$/.test(input);
    case 'GR':
      return /^\d{9}$/.test(input);
    case 'HR':
    case 'SI':
    case 'RS':
    case 'BA':
    case 'ME':
    case 'MK':
    case 'MT':
    case 'CY':
    case 'LU':
    case 'LT':
    case 'LV':
    case 'EE':
      return true;

    // Middle East & North Africa
    case 'TR':
    case 'IL':
    case 'SA':
    case 'AE':
    case 'QA':
    case 'KW':
    case 'BH':
    case 'OM':
    case 'JO':
    case 'LB':
    case 'EG':
    case 'MA':
    case 'TN':
    case 'DZ':
      return true;

    // Africa
    case 'ZA':
      return /^\d{4}\/\d{6}\/\d{2}$/.test(input);
    case 'NG':
    case 'KE':
    case 'GH':
    case 'UG':
    case 'TZ':
    case 'RW':
    case 'BW':
    case 'ZM':
    case 'ZW':
    case 'ET':
      return true;

    // Asia
    case 'CN':
      return /^[0-9A-Z]{18}$/.test(input);
    case 'IN':
      return (
        /^[A-Z]{1}\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/.test(input) ||
        /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d{1}[Z]{1}[A-Z\d]{1}$/.test(input)
      );
    case 'PK':
    case 'BD':
      return true;
    case 'JP':
      return /^\d{13}$/.test(input);
    case 'KR':
      return /^\d{3}-\d{2}-\d{5}$/.test(input);
    case 'SG':
    case 'MY':
    case 'ID':
    case 'PH':
    case 'TH':
    case 'VN':
      return true;
    case 'HK':
      return /^\d{8}$/.test(input);
    case 'TW':
    case 'LK':
    case 'NP':
      return true;

    // Oceania
    case 'AU':
      return /^\d{11}$/.test(input) || /^\d{9}$/.test(input);
    case 'NZ':
      return /^\d{13}$/.test(input) || /^\d{7}$/.test(input);

    // Eurasia
    case 'RU':
      return /^\d{10}$/.test(input) || /^\d{13}$/.test(input);
    case 'UA':
      return /^\d{8}$/.test(input);
    case 'BY':
    case 'KZ':
    case 'UZ':
    case 'GE':
    case 'AM':
    case 'AZ':
      return true;

    default:
      return true;
  }
}
